import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import ApplicationMessages from '../components/ApplicationMessages';

const HEARD_OPTIONS = [
  '', 'Google search', 'Friend / referral', 'Discord community', 'Bluesky / X / social',
  'YouTube / TikTok', 'Pokemon community group', 'Trade show / event', 'Other',
];

const USE_CASE_OPTIONS = [
  '', 'Personal collector', 'Reseller / B2B', 'Local game store',
  'Tournament organizer', 'Content creator', 'Investment / portfolio', 'Other',
];

const tokenHdr = () => {
  const t = localStorage.getItem('token') || localStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : null;
};

export default function Apply() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    business_name: '', phone: '',
    street: '', city: '', province: '', postal_code: '', country: 'Canada',
    heard_about: '', use_case: '',
    reseller_cert_url: '',
  });

  useEffect(() => {
    const h = tokenHdr();
    if (!h) { navigate('/login?next=/apply'); return; }
    fetch('/api/users/me', { headers: h }).then(r => r.ok ? r.json() : null).then(j => {
      if (j) {
        setMe(j);
        // Pre-fill phone from user record
        if (j.phone) setForm(f => ({ ...f, phone: j.phone }));
      }
    });
    fetch('/api/applications/me', { headers: h }).then(r => r.ok ? r.json() : null).then(j => {
      if (j?.application) {
        const a = j.application;
        setForm(f => ({
          ...f,
          business_name: a.business_name || '',
          phone: a.phone || f.phone,
          street: a.street || '', city: a.city || '',
          province: a.province || '', postal_code: a.postal_code || '',
          country: a.country || 'Canada',
          heard_about: a.heard_about || '', use_case: a.use_case || '',
          reseller_cert_url: a.reseller_cert_url || '',
        }));
      }
    });
  }, [navigate]);

  const upd = (patch) => setForm(f => ({ ...f, ...patch }));

  const uploadCert = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    const r = await fetch('/api/applications/upload-cert', {
      method: 'POST', headers: tokenHdr(), body: fd,
    });
    if (!r.ok) { setErr('Upload failed'); return; }
    const j = await r.json();
    upd({ reseller_cert_url: j.url });
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (step !== 3) { setStep(s => Math.min(3, s + 1)); return; }
    setErr(null); setBusy(true);
    try {
      const r = await fetch('/api/applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...tokenHdr() },
        body: JSON.stringify(form),
      });
      const j = await r.json();
      if (!r.ok) setErr(j.error || 'Submit failed');
      else setDone(true);
    } finally { setBusy(false); }
  };

  if (me?.approval_status === 'approved') {
    return (
      <div className="page" style={pageWrap}>
        <SEO title="Application — already approved" />
        <h1>You're approved ✓</h1>
        <p>Your account is fully active. <a href="/shop">Shop now →</a></p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="page" style={pageWrap}>
        <SEO title="Application submitted" />
        <h1>Application submitted</h1>
        <p style={{ color: 'var(--text-light)' }}>
          Thanks! We'll review and email you within 72 hours. You can message us below if you'd like to share anything else, and your dashboard updates the moment we approve.
        </p>

        <h2 style={{ marginTop: 32 }}>Contact the team</h2>
        <p style={{ color: 'var(--text-light)', fontSize: 13 }}>
          Send us a message and we'll reply by email + here in your dashboard.
        </p>
        <ApplicationMessages mode="customer" />

        <button className="btn btn-secondary" onClick={() => navigate('/account')} style={{ marginTop: 24 }}>
          Go to my account →
        </button>
      </div>
    );
  }

  // If they already have an application on file, show the message thread
  // alongside the resubmit form so they can clarify or ask questions.
  const hasExistingApp = !!me?.approval_status && me.approval_status !== 'approved';

  return (
    <div className="page" style={pageWrap}>
      <SEO title="Apply for an account — SilverGames" description="Apply for a SilverGames account. Approval typically within 72 hours." />

      <h1>{hasExistingApp ? 'Manage your application' : 'Complete your application'}</h1>
      <p style={{ color: 'var(--text-light)' }}>
        Your account is created — now we just need a few details so we can review and approve you.
        We'll email you within 72 hours. Pricing and checkout unlock the moment you're approved.
        {me?.approval_status === 'rejected' && me.rejection_reason && (
          <span style={{ display: 'block', marginTop: 12, padding: 12, background: '#fee2e2', borderRadius: 6, color: '#991b1b' }}>
            <strong>Previous rejection reason:</strong> {me.rejection_reason}
          </span>
        )}
        {me?.approval_status === 'needs_info' && me.rejection_reason && (
          <span style={{ display: 'block', marginTop: 12, padding: 12, background: '#fef3c7', borderRadius: 6, color: '#92400e' }}>
            <strong>We need more info:</strong> {me.rejection_reason}
          </span>
        )}
      </p>

      {hasExistingApp && (
        <section style={{ marginTop: 24, padding: 20, border: '1px solid var(--border-color)', borderRadius: 8 }}>
          <h2 style={{ marginTop: 0 }}>Messages with the team</h2>
          <p style={{ color: 'var(--text-light)', fontSize: 13 }}>
            Anything you say here goes straight to the reviewer.
          </p>
          <ApplicationMessages mode="customer" />
        </section>
      )}

      <h2 style={{ marginTop: 32 }}>{hasExistingApp ? 'Update your application' : 'Submit your application'}</h2>
      <Stepper step={step} />

      <form onSubmit={submit} style={{ marginTop: 24 }}>
        {step === 1 && (
          <fieldset style={fs}>
            <legend>About you</legend>
            <Field label="Business name (if applicable)" value={form.business_name} onChange={v => upd({ business_name: v })} />
            <Field label="Phone *" required value={form.phone} onChange={v => upd({ phone: v })} placeholder="416-555-1234" />
          </fieldset>
        )}

        {step === 2 && (
          <fieldset style={fs}>
            <legend>Address</legend>
            <Field label="Street *" required value={form.street} onChange={v => upd({ street: v })} />
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
              <Field label="City *" required value={form.city} onChange={v => upd({ city: v })} />
              <Field label="Prov *" required value={form.province} onChange={v => upd({ province: v })} placeholder="ON" />
              <Field label="Postal *" required value={form.postal_code} onChange={v => upd({ postal_code: v })} placeholder="M5V 2T6" />
            </div>
            <Field label="Country" value={form.country} onChange={v => upd({ country: v })} />
          </fieldset>
        )}

        {step === 3 && (
          <fieldset style={fs}>
            <legend>How will you use SilverGames?</legend>
            <Select label="How did you hear about us?" value={form.heard_about} onChange={v => upd({ heard_about: v })} options={HEARD_OPTIONS} />
            <Select label="Primary use case *" value={form.use_case} onChange={v => upd({ use_case: v })} options={USE_CASE_OPTIONS} />
            <div style={{ marginTop: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
                Reseller certificate <small style={{ color: 'var(--text-light)' }}>(optional, PDF/JPG/PNG, ≤ 4MB)</small>
              </label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => uploadCert(e.target.files?.[0])} />
              {form.reseller_cert_url && (
                <small style={{ color: 'var(--color-success)', display: 'block', marginTop: 4 }}>
                  ✓ Uploaded · <a href={form.reseller_cert_url} target="_blank" rel="noreferrer">view</a>
                </small>
              )}
            </div>
          </fieldset>
        )}

        {err && <div style={{ padding: 12, background: 'var(--color-danger-bg, #fee2e2)', color: 'var(--color-danger, #991b1b)', borderRadius: 6, marginTop: 12 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 24, justifyContent: 'space-between' }}>
          {step > 1 ? (
            <button type="button" className="btn" onClick={() => setStep(s => s - 1)}>← Back</button>
          ) : <span />}
          {step < 3 ? (
            <button type="button" className="btn btn-primary" onClick={() => setStep(s => s + 1)}>
              Next →
            </button>
          ) : (
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Submitting…' : 'Submit application →'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Stepper({ step }) {
  const steps = ['About you', 'Address', 'Usage'];
  return (
    <div style={{ display: 'flex', gap: 0, marginTop: 24, marginBottom: 8, fontSize: 13 }}>
      {steps.map((label, i) => {
        const n = i + 1, on = step >= n;
        return (
          <div key={n} style={{ flex: 1, textAlign: 'center', padding: '8px 0', borderBottom: `3px solid ${on ? 'var(--color-primary, #3b4cca)' : 'var(--border-color, #e2e8f0)'}`, color: on ? 'inherit' : 'var(--text-light)' }}>
            {n}. {label}
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, value, onChange, required, placeholder }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{label}</label>
      <input type="text" required={required} placeholder={placeholder} value={value || ''}
        onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: 10, fontSize: 14, borderRadius: 6, border: '1px solid var(--border-color)' }} />
    </div>
  );
}
function Select({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{label}</label>
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: 10, fontSize: 14, borderRadius: 6, border: '1px solid var(--border-color)' }}>
        {options.map(o => <option key={o} value={o}>{o || 'Select…'}</option>)}
      </select>
    </div>
  );
}

const pageWrap = { maxWidth: 640, margin: '0 auto', padding: '2rem 1rem' };
const fs = { border: '1px solid var(--border-color)', borderRadius: 8, padding: 20 };
